import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FiLogOut, FiSettings, FiVideo } from 'react-icons/fi';
import { TfiWallet } from 'react-icons/tfi';
import moment from 'moment';
import { getAppointmentList } from '../../store/appointment/appointmentActions';
import { logoutUser } from '../../store/auth/authActions';
import { getImage } from '../../utils';
import { useCallSession } from '../../pages/video/callSessionContext';
import { COURSE_DETAILS_URL } from '../app/routes';
import { ProfileRole } from '../../store/profile/profileSlice';
import { getWalletStats } from '../../store/wallet/walletActions';

// ---------------------------------------------------------------------------
// Route → human-readable page title
// ---------------------------------------------------------------------------
const ROUTE_TITLES = [
    { pattern: /^\/courses\/detail\/[^/]+$/, label: 'Course Details' },
    { pattern: /^\/courses\/chat$/, label: 'Lesson call' },
    { pattern: /^\/courses$/, label: 'Courses' },
    { pattern: /^\/admin\/courses$/, label: 'All Courses' },
    { pattern: /^\/tutor\/[^/]+$/, label: 'Tutor Profile' },
    { pattern: /^\/tutors$/, label: 'Tutors' },
    { pattern: /^\/chat(\/[^/]+)?$/, label: 'Chat' },
    { pattern: /^\/calendar$/, label: 'Calendar' },
    { pattern: /^\/notifications$/, label: 'Notifications' },
    { pattern: /^\/wallet$/, label: 'Transactions' },
    { pattern: /^\/withdraw$/, label: 'Withdraw' },
    { pattern: /^\/settings$/, label: 'Settings' },
    { pattern: /^\/me$/, label: 'My Profile' },
    { pattern: /^\/order\/payment\/[^/]+$/, label: 'Payment' },
    { pattern: /^\/order\/detail\/[^/]+$/, label: 'Order details' },
    { pattern: /^\/$/, label: 'Dashboard' },
];

const getPageTitle = (pathname) => {
    for (const { pattern, label } of ROUTE_TITLES) {
        if (pattern.test(pathname)) return label;
    }
    return '';
};

// ---------------------------------------------------------------------------
// PageTitle – left side of the TopBar
// ---------------------------------------------------------------------------
const PageTitle = () => {
    const { pathname } = useLocation();
    const title = getPageTitle(pathname);
    if (!title) return null;
    return (
        <div className="flex flex-col justify-center min-w-0">
            <h1 className="text-base font-bold text-nt-blue leading-tight truncate">{title}</h1>
        </div>
    );
};

// ---------------------------------------------------------------------------
// LocalClock – updates every minute, zero extra re-renders
// ---------------------------------------------------------------------------
const LocalClock = () => {
    const [time, setTime] = useState(() => new Date());

    useEffect(() => {
        const msUntilNextMinute =
            (60 - new Date().getSeconds()) * 1000 - new Date().getMilliseconds();
        let interval;
        const timeout = setTimeout(() => {
            setTime(new Date());
            interval = setInterval(() => setTime(new Date()), 60_000);
        }, msUntilNextMinute);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
        };
    }, []);

    const hours = time.getHours().toString().padStart(2, '0');
    const minutes = time.getMinutes().toString().padStart(2, '0');
    const day = time.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });

    return (
        <div className="hidden sm:flex flex-col items-end leading-tight select-none shrink-0">
            <span className="text-sm font-bold text-nt-blue tabular-nums tracking-tight">
                {hours}:{minutes}
            </span>
            <span className="text-[11px] text-nt-grey mt-px">{day}</span>
        </div>
    );
};

// ---------------------------------------------------------------------------
// JoinCourseBtn – pulsing button within the 5-min join window
// ---------------------------------------------------------------------------
const POLL_MS = 60_000;
const REFETCH_MS = 5 * 60_000;

/** Returns true when the appointment is within its join window (-5 min → +50 min from start). */
const isInJoinWindow = (appt) => {
    // Only pending/confirmed appointments (status === -1) are joinable
    if (appt?.status !== -1) return false;
    // Use dateUtc (added by the list endpoint) or fall back to date
    const rawDate = appt.dateUtc ?? appt.date;
    if (!rawDate) return false;
    const start = moment(rawDate);
    const openAt = moment(start).subtract(5, 'minutes');
    const closeAt = moment(start).add(50, 'minutes');
    return moment().isBetween(openAt, closeAt, undefined, '[]');
};

const JoinCourseBtn = ({ hide }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [appointments, setAppointments] = useState([]);
    const [activeAppt, setActiveAppt] = useState(null);

    const fetchList = useCallback(() => {
        dispatch(getAppointmentList({}))
            .unwrap()
            .then((list) => setAppointments(Array.isArray(list) ? list : []))
            .catch(() => {});
    }, [dispatch]);

    useEffect(() => {
        fetchList();
        const refetch = setInterval(fetchList, REFETCH_MS);
        return () => clearInterval(refetch);
    }, [fetchList]);

    // Hide Live pill immediately after cancel/reschedule (navigation) or tab return
    useEffect(() => {
        fetchList();
    }, [location.pathname, fetchList]);

    useEffect(() => {
        const onVis = () => {
            if (!document.hidden) fetchList();
        };
        window.addEventListener('focus', fetchList);
        document.addEventListener('visibilitychange', onVis);
        return () => {
            window.removeEventListener('focus', fetchList);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [fetchList]);

    useEffect(() => {
        const evaluate = () => setActiveAppt(appointments.find(isInJoinWindow) ?? null);
        evaluate();
        const tick = setInterval(evaluate, POLL_MS);
        return () => clearInterval(tick);
    }, [appointments]);

    if (hide || !activeAppt) return null;

    return (
        <motion.button
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.88 }}
            transition={{ type: 'spring', stiffness: 420, damping: 26 }}
            onClick={() => navigate(`${COURSE_DETAILS_URL}/${activeAppt._id}`)}
            className="relative flex items-center gap-2 bg-nt-blue text-white text-xs font-semibold px-3.5 py-2 rounded-xl shadow-lg hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-nt-blue focus-visible:ring-offset-2 transition-opacity whitespace-nowrap shrink-0"
        >
            <span className="absolute inset-0 rounded-xl animate-ping bg-nt-blue opacity-25 pointer-events-none" />
            <FiVideo size={14} className="shrink-0" aria-hidden />
            <span className="hidden xs:inline">Join with</span>
            <span className="font-bold capitalize truncate max-w-[120px]">{activeAppt.fullname}</span>
        </motion.button>
    );
};

// ---------------------------------------------------------------------------
// AvatarDropdown – avatar pill → dropdown with user info, settings, logout
// ---------------------------------------------------------------------------
const dropdownVariants = {
    hidden: { opacity: 0, y: -8, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { type: 'spring', stiffness: 420, damping: 28 },
    },
    exit: { opacity: 0, y: -8, scale: 0.95, transition: { duration: 0.12 } },
};

const AvatarDropdown = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { currentProfile } = useSelector((state) => state.profile);
    const [open, setOpen] = useState(false);
    const [logoutOpen, setLogoutOpen] = useState(false);
    const cancelRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    useEffect(() => {
        if (!logoutOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') setLogoutOpen(false); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        cancelRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [logoutOpen]);

    const confirmLogout = () => {
        setLogoutOpen(false);
        dispatch(logoutUser());
    };

    if (!user) return null;

    const roleLabel = currentProfile?.role
        ? String(currentProfile.role).charAt(0).toUpperCase() + String(currentProfile.role).slice(1)
        : '';

    return (
        <>
            <div ref={containerRef} className="relative shrink-0">
                {/* Avatar trigger */}
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-haspopup="true"
                    aria-expanded={open}
                    aria-label="Profile menu"
                    className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full bg-nt-grey-light/50 hover:bg-nt-grey-light border border-transparent hover:border-nt-grey-light focus:outline-none focus-visible:ring-2 focus-visible:ring-nt-blue focus-visible:ring-offset-1 transition-all"
                >
                    <img
                        src={getImage(user.avatar, 'AVATAR')}
                        alt={user.firstName}
                        className="w-7 h-7 rounded-full object-cover shrink-0 ring-2 ring-white"
                    />
                    <span className="hidden sm:block text-xs font-semibold text-nt-blue capitalize leading-none truncate max-w-[90px]">
                        {user.firstName}
                    </span>
                    <motion.svg
                        animate={{ rotate: open ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="hidden sm:block w-3.5 h-3.5 text-nt-grey shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        aria-hidden
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </motion.svg>
                </button>

                <AnimatePresence>
                    {open && (
                        <motion.div
                            variants={dropdownVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="absolute right-0 top-[calc(100%+8px)] w-64 bg-white rounded-2xl shadow-2xl shadow-black/10 ring-1 ring-black/5 overflow-hidden z-50"
                        >
                            {/* Identity header */}
                            <div className="flex items-center gap-3 px-4 py-4 bg-gradient-to-br from-nt-blue/5 to-transparent border-b border-nt-grey-light/50">
                                <div className="relative shrink-0">
                                    <img
                                        src={getImage(user.avatar, 'AVATAR')}
                                        alt={user.firstName}
                                        className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm"
                                    />
                                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-nt-blue capitalize truncate">
                                        {user.firstName} {user.lastName}
                                    </p>
                                    {roleLabel && (
                                        <span className="inline-flex items-center mt-1 text-[10px] font-semibold text-nt-blue bg-nt-blue/10 px-2 py-0.5 rounded-full capitalize">
                                            {roleLabel}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Menu items */}
                            <div className="py-1.5">
                                <Link
                                    to="/settings"
                                    onClick={() => setOpen(false)}
                                    className="no-underline flex items-center gap-3 mx-2 px-3 py-2.5 text-sm font-medium text-nt-blue rounded-xl hover:bg-nt-grey-light/40 transition-colors"
                                >
                                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-nt-blue/8">
                                        <FiSettings size={14} aria-hidden />
                                    </span>
                                    Settings
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => { setOpen(false); setLogoutOpen(true); }}
                                    className="w-full flex items-center gap-3 mx-2 px-3 py-2.5 text-sm font-medium text-nt-red rounded-xl hover:bg-red-50 transition-colors"
                                    style={{ width: 'calc(100% - 16px)' }}
                                >
                                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50">
                                        <FiLogOut size={14} aria-hidden />
                                    </span>
                                    Sign out
                                </button>
                            </div>
                            <div className="h-1.5" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Logout confirmation portal */}
            {typeof document !== 'undefined' &&
                createPortal(
                    <AnimatePresence>
                        {logoutOpen && (
                            <div
                                className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                                role="presentation"
                            >
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute inset-0 bg-black/40 backdrop-blur-[2px] cursor-default"
                                    onClick={() => setLogoutOpen(false)}
                                    aria-hidden
                                />
                                <motion.div
                                    role="dialog"
                                    aria-modal="true"
                                    aria-labelledby="logout-title"
                                    aria-describedby="logout-desc"
                                    initial={{ opacity: 0, scale: 0.96, y: 10 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.96, y: 10 }}
                                    transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                                    className="relative w-full max-w-[400px] rounded-2xl bg-white shadow-2xl shadow-black/15 ring-1 ring-black/5 overflow-hidden"
                                >
                                    <div className="p-6 sm:p-8">
                                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-nt-blue/10 text-nt-blue">
                                            <FiLogOut size={26} strokeWidth={2} aria-hidden />
                                        </div>
                                        <h2
                                            id="logout-title"
                                            className="text-center text-lg font-semibold text-nt-blue sm:text-xl"
                                        >
                                            Sign out?
                                        </h2>
                                        <p
                                            id="logout-desc"
                                            className="mt-2 text-center text-sm text-nt-grey leading-relaxed"
                                        >
                                            You will need to sign in again to continue.
                                        </p>
                                        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                            <button
                                                ref={cancelRef}
                                                type="button"
                                                onClick={() => setLogoutOpen(false)}
                                                className="w-full sm:w-auto min-h-[44px] rounded-xl border-2 border-nt-grey-light px-5 py-2.5 text-sm font-semibold text-nt-blue hover:bg-nt-grey-light/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-nt-blue focus-visible:ring-offset-2 transition-colors sm:min-w-[120px]"
                                            >
                                                Stay signed in
                                            </button>
                                            <button
                                                type="button"
                                                onClick={confirmLogout}
                                                className="w-full sm:w-auto min-h-[44px] rounded-xl bg-nt-red px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-nt-red focus-visible:ring-offset-2 transition-opacity sm:min-w-[120px]"
                                            >
                                                Sign out
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        )}
                    </AnimatePresence>,
                    document.body,
                )}
        </>
    );
};

// ---------------------------------------------------------------------------
// BalancePill – compact wallet balance for tutors
// ---------------------------------------------------------------------------
const BalancePill = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentProfile } = useSelector((state) => state.profile);
    const { balance } = useSelector((state) => state.wallet);
    const isTutor = currentProfile?.role === ProfileRole.TUTOR;

    useEffect(() => {
        if (isTutor) dispatch(getWalletStats());
    }, [isTutor, dispatch]);

    if (!isTutor) return null;

    return (
        <button
            type="button"
            onClick={() => navigate('/wallet')}
            className="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full bg-nt-grey-light/50 hover:bg-nt-grey-light border border-transparent hover:border-nt-grey-light transition-colors shrink-0"
            title="Balance"
        >
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-nt-blue/10 text-nt-blue">
                <TfiWallet size={12} />
            </span>
            <span className="text-xs font-semibold text-nt-blue whitespace-nowrap">{balance} €</span>
        </button>
    );
};

// ---------------------------------------------------------------------------
// TopBar – rounded floating card layout
// ---------------------------------------------------------------------------
const TopBar = () => {
    const { callSession } = useCallSession() || {};
    const isInActiveCall = Boolean(callSession);

    return (
        <header className="mx-4 xl:mt-4 flex items-center justify-between gap-3 px-4 py-2.5 bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.07)] border border-nt-grey-light/60">
            {/* Left: page title */}
            <PageTitle />

            {/* Right: actions */}
            <div className="flex items-center gap-3 ml-auto">
                <AnimatePresence> 
                    <JoinCourseBtn hide={isInActiveCall} />
                </AnimatePresence>

                {/* Divider */}
                <div className="hidden sm:block w-px h-5 bg-nt-grey-light/80 shrink-0" />

                <BalancePill />

                <LocalClock />

                <div className="w-px h-5 bg-nt-grey-light/80 shrink-0" />

                <AvatarDropdown />
            </div>
        </header>
    );
};

export { TopBar };
